const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.min.mjs';
const PDFJS_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';

let pdfjsPromise;
function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import(PDFJS_URL).then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      return pdfjs;
    });
  }
  return pdfjsPromise;
}

class PdfViewer extends HTMLElement {
  connectedCallback() {
    this.frame = this.querySelector('[data-frame]');
    this.status = this.querySelector('[data-status]');
    this.openLink = this.querySelector('[data-open]');
    this.downloadLink = this.querySelector('[data-download]');

    this.querySelectorAll('.pdf-viewer__tab').forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));
    });

    this.frame.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.turn(-1);
      if (event.key === 'ArrowRight') this.turn(1);
    });

    // Horizontal swipe turns the page, vertical movement is left to the browser.
    this.frame.addEventListener('touchstart', (event) => {
      this.touchStart = event.touches[0];
    }, { passive: true });
    this.frame.addEventListener('touchend', (event) => {
      if (!this.touchStart) return;
      const dx = event.changedTouches[0].clientX - this.touchStart.clientX;
      const dy = event.changedTouches[0].clientY - this.touchStart.clientY;
      this.touchStart = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.turn(dx < 0 ? 1 : -1);
    });

    // Refit the page when the viewer changes size (rotation, window resize).
    let lastWidth = 0;
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.frame.clientWidth;
      if (!this.pdf || !this.canvas || width === lastWidth) return;
      lastWidth = width;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.showPage(this.pdf, this.pageNumber), 150);
    });
    this.resizeObserver.observe(this.frame);

    // Load PDF.js only once the viewer is close to the viewport.
    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.visibilityObserver.disconnect();
        this.started = true;
        this.load(this.frame.dataset.src);
      },
      { rootMargin: '300px' }
    );
    this.visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.pdf?.destroy();
  }

  selectTab(tab) {
    if (tab.getAttribute('aria-selected') === 'true') return;
    this.querySelectorAll('.pdf-viewer__tab').forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    const src = tab.dataset.src;
    this.frame.dataset.src = src;
    this.openLink.href = tab.dataset.openSrc;
    this.downloadLink.href = tab.dataset.downloadSrc;
    // Viewers further down the page pick up the new source when they scroll into view.
    if (this.started) this.load(src);
  }

  selectLocale(locale) {
    const tab = [...this.querySelectorAll('.pdf-viewer__tab')].find((t) => t.dataset.locale === locale);
    if (tab) this.selectTab(tab);
  }

  showMessage(text) {
    this.frame.replaceChildren();
    const message = document.createElement('p');
    message.className = 'pdf-viewer__message';
    message.textContent = text;
    this.frame.append(message);
  }

  async load(src) {
    const loadId = (this.loadId = (this.loadId || 0) + 1);
    this.renderTask?.cancel();
    this.pdf?.destroy();
    this.pdf = null;
    this.status.textContent = '';

    // Google Drive blocks cross-origin fetches, so use its own embeddable preview instead.
    if (src.startsWith('https://drive.google.com/')) {
      this.frame.classList.add('pdf-viewer__frame--embed');
      const iframe = document.createElement('iframe');
      iframe.className = 'pdf-viewer__embed';
      iframe.src = src;
      iframe.title = this.querySelector('.pdf-viewer__tab[aria-selected="true"]')?.textContent.trim() || 'PDF';
      iframe.allow = 'autoplay';
      this.frame.replaceChildren(iframe);
      return;
    }

    this.frame.classList.remove('pdf-viewer__frame--embed');
    this.canvas = null;
    this.showMessage(this.frame.dataset.loadingText);

    try {
      const pdfjs = await loadPdfjs();
      const pdf = await pdfjs.getDocument(src).promise;
      if (loadId !== this.loadId) return pdf.destroy();
      this.pdf = pdf;
      this.status.textContent = this.frame.dataset.pagesText.replace('[count]', pdf.numPages);
      this.createPages(pdf);
    } catch (error) {
      if (loadId !== this.loadId) return;
      console.error('PDF viewer:', error);
      this.showMessage(this.frame.dataset.errorText);
    }
  }

  // One page at a time, flipped with the arrows, swipe or the keyboard.
  createPages(pdf) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'pdf-viewer__page';

    const prev = this.createNavButton('prev', this.frame.dataset.prevLabel, -1);
    const next = this.createNavButton('next', this.frame.dataset.nextLabel, 1);
    this.counter = document.createElement('span');
    this.counter.className = 'pdf-viewer__counter';
    this.navButtons = { prev, next };

    this.frame.replaceChildren(this.canvas, prev, next, this.counter);
    this.frame.tabIndex = 0;
    this.showPage(pdf, 1);
  }

  createNavButton(direction, label, step) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `pdf-viewer__nav pdf-viewer__nav--${direction}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>';
    button.addEventListener('click', () => this.turn(step));
    return button;
  }

  turn(step) {
    if (!this.pdf) return;
    const target = this.pageNumber + step;
    if (target < 1 || target > this.pdf.numPages) return;
    this.showPage(this.pdf, target);
  }

  async showPage(pdf, number) {
    this.pageNumber = number;
    this.counter.textContent = `${number} ${this.frame.dataset.ofLabel} ${pdf.numPages}`;
    this.navButtons.prev.disabled = number === 1;
    this.navButtons.next.disabled = number === pdf.numPages;

    this.renderTask?.cancel();
    try {
      const page = await pdf.getPage(number);
      if (this.pdf !== pdf || this.pageNumber !== number) return;

      // Fit the whole page inside the frame.
      const base = page.getViewport({ scale: 1 });
      const style = getComputedStyle(this.frame);
      const boxWidth = this.frame.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const boxHeight = this.frame.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      // On phones the frame grows with the page, so only the width limits it.
      const phone = window.matchMedia('(max-width: 749px)').matches;
      const fit = phone ? boxWidth / base.width : Math.min(boxWidth / base.width, boxHeight / base.height);
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: fit * ratio });

      // Draw off screen first so the old page stays visible until the new one is ready.
      const buffer = document.createElement('canvas');
      buffer.width = Math.floor(viewport.width);
      buffer.height = Math.floor(viewport.height);
      this.renderTask = page.render({ canvasContext: buffer.getContext('2d'), viewport });
      await this.renderTask.promise;

      this.canvas.width = buffer.width;
      this.canvas.height = buffer.height;
      this.canvas.style.width = `${Math.floor(base.width * fit)}px`;
      this.canvas.style.height = `${Math.floor(base.height * fit)}px`;
      this.canvas.getContext('2d').drawImage(buffer, 0, 0);

      if (number < pdf.numPages) pdf.getPage(number + 1);
    } catch (error) {
      if (error?.name !== 'RenderingCancelledException' && this.pdf === pdf) console.error('PDF viewer page:', error);
    }
  }
}

if (!customElements.get('pdf-viewer')) customElements.define('pdf-viewer', PdfViewer);

// One row of language buttons that switches every PDF viewer on the page.
class PdfLanguageSwitcher extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-locale]').forEach((button) => {
      button.addEventListener('click', () => {
        this.querySelectorAll('[data-locale]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
        document.querySelectorAll('pdf-viewer').forEach((viewer) => viewer.selectLocale(button.dataset.locale));
      });
    });
  }
}

if (!customElements.get('pdf-language-switcher')) customElements.define('pdf-language-switcher', PdfLanguageSwitcher);
